import {
  ref,
  set,
  update,
  push,
  remove,
  onChildAdded,
  onChildChanged,
  type Database,
  type Unsubscribe,
} from 'firebase/database';
import { getRealtimeDb, nowServer } from '../firebase/rtdb';
import type {
  CombatEvent,
  PlayerArenaState,
  PositionUpdate,
} from '@/types/realtime';
import type { Player } from '@/types';
import { getArenaPlayerStartPosition } from '@/constants/arena';

class ArenaRealtimeService {
  private db: Database | null = null;
  private activeListeners: Unsubscribe[] = [];
  /** Set to false after a failed write proves RTDB is unreachable */
  private healthy = true;
  /** Callbacks notified when RTDB becomes unhealthy */
  private unhealthyCallbacks: Array<() => void> = [];

  private getDb(): Database | null {
    if (!this.db) {
      this.db = getRealtimeDb();
    }
    return this.db;
  }

  /** Check if RTDB is available AND healthy (no prior write failures) */
  isAvailable(): boolean {
    return this.healthy && this.getDb() !== null;
  }

  /**
   * Register a callback that fires when RTDB becomes unhealthy.
   * Returns an unsubscribe function.
   */
  onUnhealthy(cb: () => void): () => void {
    this.unhealthyCallbacks.push(cb);
    return () => {
      this.unhealthyCallbacks = this.unhealthyCallbacks.filter(c => c !== cb);
    };
  }

  /** Mark RTDB as unhealthy — all subsequent calls will fall back to Firestore */
  private markUnhealthy(reason: string): void {
    if (this.healthy) {
      this.healthy = false;
      console.warn(`⚠️ RTDB marked unhealthy: ${reason}. Falling back to Firestore for arena sync.`);
      this.detachAllListeners();
      for (const cb of this.unhealthyCallbacks) {
        try { cb(); } catch { /* observer error is non-fatal */ }
      }
    }
  }

  /**
   * Public API to force a fallback to Firestore.
   * This keeps all clients aligned on a single RTDB health source of truth.
   */
  forceFallback(reason: string = 'fallback requested'): void {
    this.markUnhealthy(reason);
  }

  // ─── Outbound ────────────────────────────────────────────

  /**
   * Write local player position. Fire-and-forget, no await needed.
   *
   * Timestamps are server-aligned via `nowServer()` so receivers on other
   * clocks can still interpret inter-sample gaps consistently. Optional
   * `vx`/`vy` let the sender publish its authoritative velocity instead of
   * forcing receivers to infer it from position deltas (which amplifies any
   * clock skew).
   */
  writePosition(
    sessionId: string,
    playerId: string,
    pos: { x: number; y: number; vx?: number; vy?: number; dir?: 'left' | 'right' }
  ): void {
    const db = this.getDb();
    if (!db || !this.healthy) return;

    const posRef = ref(db, `arenaState/${sessionId}/positions/${playerId}`);
    const update: PositionUpdate = { x: pos.x, y: pos.y, ts: nowServer() };
    if (typeof pos.vx === 'number' && Number.isFinite(pos.vx)) update.vx = pos.vx;
    if (typeof pos.vy === 'number' && Number.isFinite(pos.vy)) update.vy = pos.vy;
    if (pos.dir === 'left' || pos.dir === 'right') update.dir = pos.dir;
    set(posRef, update).catch((err) => {
      this.markUnhealthy(`position write failed: ${err?.message ?? err}`);
    });
  }

  /** Push a combat event (grenade throw, net throw, explosion, etc.) */
  pushCombatEvent(sessionId: string, event: CombatEvent): void {
    const db = this.getDb();
    if (!db || !this.healthy) return;

    const eventsRef = ref(db, `arenaState/${sessionId}/combat/events`);
    push(eventsRef, event).catch((err) => {
      this.markUnhealthy(`combat event push failed: ${err?.message ?? err}`);
    });
  }

  /** Write per-player arena state (charges, immobilization, turbo). */
  writePlayerState(sessionId: string, playerId: string, state: Partial<PlayerArenaState>): void {
    const db = this.getDb();
    if (!db || !this.healthy) return;

    const stateRef = ref(db, `arenaState/${sessionId}/playerState/${playerId}`);
    update(stateRef, state).catch((err) => {
      this.markUnhealthy(`player state write failed: ${err?.message ?? err}`);
    });
  }

  // ─── Inbound ─────────────────────────────────────────────

  /** Subscribe to position changes for all players in a session. */
  onPositionChanges(
    sessionId: string,
    callback: (playerId: string, pos: PositionUpdate) => void
  ): () => void {
    const db = this.getDb();
    if (!db) return () => {};

    const positionsRef = ref(db, `arenaState/${sessionId}/positions`);

    const handleChange = (snapshot: import('firebase/database').DataSnapshot) => {
      if (!snapshot.key || !snapshot.val()) return;
      const data = snapshot.val() as PositionUpdate;
      callback(snapshot.key, data);
    };

    const unsubChanged = onChildChanged(positionsRef, handleChange);
    const unsubAdded = onChildAdded(positionsRef, handleChange);

    const unsub = () => {
      unsubChanged();
      unsubAdded();
    };
    this.activeListeners.push(unsub);
    return unsub;
  }

  /** Subscribe to new combat events in a session. */
  onCombatEvent(
    sessionId: string,
    callback: (event: CombatEvent) => void
  ): () => void {
    const db = this.getDb();
    if (!db) return () => {};

    const eventsRef = ref(db, `arenaState/${sessionId}/combat/events`);

    const unsub = onChildAdded(eventsRef, (snapshot) => {
      const data = snapshot.val() as CombatEvent | null;
      if (data) {
        callback(data);
      }
    });

    this.activeListeners.push(unsub);
    return unsub;
  }

  /** Subscribe to player state changes for all players. */
  onPlayerStateChanges(
    sessionId: string,
    callback: (playerId: string, state: Partial<PlayerArenaState>) => void
  ): () => void {
    const db = this.getDb();
    if (!db) return () => {};

    const playerStateRef = ref(db, `arenaState/${sessionId}/playerState`);

    const handleChange = (snapshot: import('firebase/database').DataSnapshot) => {
      if (!snapshot.key || !snapshot.val()) return;
      callback(snapshot.key, snapshot.val() as Partial<PlayerArenaState>);
    };

    const unsubChanged = onChildChanged(playerStateRef, handleChange);
    const unsubAdded = onChildAdded(playerStateRef, handleChange);

    const unsub = () => {
      unsubChanged();
      unsubAdded();
    };
    this.activeListeners.push(unsub);
    return unsub;
  }

  // ─── Lifecycle ───────────────────────────────────────────

  /**
   * Initialize RTDB arena state for a session.
   * Called when session enters 'running' phase.
   * Seeds initial player positions and state.
   */
  async initSession(sessionId: string, players: Player[]): Promise<boolean> {
    const db = this.getDb();
    if (!db || !this.healthy) return false;

    const sessionRef = ref(db, `arenaState/${sessionId}`);

    const positions: Record<string, PositionUpdate> = {};
    const playerState: Record<string, PlayerArenaState> = {};

    for (const player of players) {
      if (player.id.startsWith('ai-player-')) continue;
      const startPosition = getArenaPlayerStartPosition();

      positions[player.id] = {
        x: startPosition.x,
        y: startPosition.y,
        ts: nowServer(),
      };

      playerState[player.id] = {
        grenadeCharges: player.grenadeCharges ?? 0,
        netCharges: player.netCharges ?? 0,
        isImmobilized: player.isImmobilized ?? false,
        immobilizedUntil: player.immobilizedUntil ?? null,
        turboAvailable: player.turboAvailable ?? false,
        turboUsed: player.turboUsed ?? false,
        facingDirection: 'right',
      };
    }

    try {
      // Race the write against a timeout — Firebase SDK will hang forever on unreachable RTDB
      const INIT_TIMEOUT_MS = 5000;
      const writePromise = set(sessionRef, {
        positions,
        playerState,
        combat: { events: {} },
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('RTDB connection timed out')), INIT_TIMEOUT_MS)
      );

      await Promise.race([writePromise, timeoutPromise]);
      console.log('✅ RTDB arena session initialized for', sessionId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.markUnhealthy(`initSession failed: ${message}`);
      return false;
    }
  }

  /**
   * Verify RTDB connectivity with a quick probe write.
   * Called by each client (student, spectator) when entering the arena.
   * If the probe fails within 3 seconds, marks RTDB as unhealthy so all
   * operations fall back to Firestore.
   */
  async verifyConnection(sessionId: string): Promise<boolean> {
    const db = this.getDb();
    if (!db || !this.healthy) return false;

    const probeRef = ref(db, `arenaState/${sessionId}/_probe`);
    const PROBE_TIMEOUT_MS = 3000;

    try {
      const writePromise = set(probeRef, Date.now());
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('RTDB probe timed out')), PROBE_TIMEOUT_MS)
      );
      await Promise.race([writePromise, timeoutPromise]);
      // Clean up probe value
      set(probeRef, null).catch(() => {});
      console.log('✅ RTDB connection verified for session', sessionId);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.markUnhealthy(`connection probe failed: ${message}`);
      return false;
    }
  }

  /**
   * Clean up RTDB arena state for a session.
   * Called by the teacher's client when the session leaves 'running' phase.
   * Students only detach listeners.
   */
  async cleanupSession(sessionId: string): Promise<void> {
    const db = this.getDb();
    if (!db) return;

    const sessionRef = ref(db, `arenaState/${sessionId}`);
    await remove(sessionRef).catch(() => {
      // Cleanup is best-effort
    });
  }

  /** Detach all active listeners for this service instance. */
  detachAllListeners(): void {
    for (const unsub of this.activeListeners) {
      unsub();
    }
    this.activeListeners = [];
  }
}

export { ArenaRealtimeService };
